import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildFAQPageSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check } from "lucide-react";
import type { GeoFeaturePageData } from "./geo-feature-pages";

export default function GeoFeaturePage({ page }: { page: GeoFeaturePageData }) {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, [
    { name: "Início", path: "/" },
    { name: page.breadcrumbName, path: page.path },
  ]);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, page.description);
  const faqSchema = buildFAQPageSchema(page.faq);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={page.title} description={page.description} canonicalPath={page.path} />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema, faqSchema]} />
      <Header />

      <main className="flex-1">
        <article className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-balance">{page.h1}</h1>
          <p className="text-lg text-muted-foreground mb-12">{page.intro}</p>

          {page.sections.map((section) => (
            <section key={section.heading} className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 48)} className="text-muted-foreground mb-4">
                  {paragraph}
                </p>
              ))}
              {section.bullets ? (
                <ul className="space-y-3 text-muted-foreground">
                  {section.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}

          {page.faq.length > 0 ? (
            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">Perguntas frequentes</h2>
              <dl className="space-y-6">
                {page.faq.map((item) => (
                  <div key={item.question}>
                    <dt className="font-medium text-foreground mb-1">{item.question}</dt>
                    <dd className="text-muted-foreground">{item.answer}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}

          <nav className="text-sm text-muted-foreground mb-6" aria-label="Páginas relacionadas">
            {page.related.map((item, index) => (
              <span key={item.to}>
                {index > 0 ? " · " : null}
                <Link to={item.to} className="text-primary hover:underline">
                  {item.label}
                </Link>
              </span>
            ))}
          </nav>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/planos">
                Ver planos <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
